import React from "react";
import { SignInButton as ClerkSignInButton, UserButton, useAuth, useUser } from "@clerk/clerk-react";

/**
 * SignInButton Component
 *
 * This component handles user authentication state representation in the UI header.
 * It supports three distinct states:
 * 1. Loading: When Clerk is loading session state, it shows a disabled "Connecting..." indicator.
 * 2. SignedOut: Shows a stylized "Sign In" button that triggers the Clerk login modal.
 * 3. SignedIn: Renders Clerk's UserButton to display the user's avatar and open their profile/sign-out menu.
 *
 * Features:
 * - Employs CSS custom properties (variables) from `index.css` for consistent dark theme aesthetic.
 * - Verbose comments instruct non-coders how to adjust appearance.
 */
export const SignInButton: React.FC = () => {
  // Check auth load and sign-in status from Clerk hooks
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  // --- STYLING CONSTANTS ---
  // If you want to customize the button, tweak these parameters:
  const buttonStyle: React.CSSProperties = {
    backgroundColor: "transparent",
    border: "1px solid var(--color-accent)",
    color: "var(--color-accent)",
    borderRadius: "var(--border-radius-md)",
    padding: "0.5rem 1.2rem",
    fontSize: "0.95rem",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all var(--transition-speed) ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  };

  const loadingStyle: React.CSSProperties = {
    ...buttonStyle,
    border: "1px solid var(--color-border)",
    color: "var(--color-text-muted)",
    cursor: "not-allowed",
  };

  // 1. LOADING STATE
  // If Clerk hasn't initialized yet, render a skeleton/connecting indicator.
  if (!isLoaded) {
    return (
      <button 
        style={loadingStyle} 
        disabled 
        aria-label="Loading authentication status"
        data-testid="signin-loading"
      >
        {/* Subtle spinner element */}
        <span 
          style={{
            width: "14px",
            height: "14px",
            border: "2px solid var(--color-text-muted)",
            borderTopColor: "transparent",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
            display: "inline-block",
          }} 
        />
        Connecting...
      </button>
    );
  }

  // 2. SIGNED IN STATE
  // Render Clerk's default avatar user button with sign-out option.
  if (isSignedIn && user) {
    return (
      <div 
        style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
        data-testid="signin-signed-in"
      >
        <span 
          style={{ 
            color: "var(--color-text-secondary)", 
            fontSize: "0.9rem", 
            fontWeight: 500 
          }}
        >
          {user.fullName || user.primaryEmailAddress?.emailAddress}
        </span>
        <UserButton afterSignOutUrl="/" />
      </div>
    );
  }

  // 3. SIGNED OUT STATE (Default fallback)
  // Render the Clerk SignInButton wrapper that opens the Google/Email login modal.
  return (
    <ClerkSignInButton mode="modal">
      <button
        style={buttonStyle}
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = "var(--color-accent-glow)";
          e.currentTarget.style.boxShadow = "0 0 8px var(--color-accent-glow)";
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = "transparent";
          e.currentTarget.style.boxShadow = "none";
        }}
        data-testid="signin-signed-out"
      >
        Sign In
      </button>
    </ClerkSignInButton>
  );
};

export default SignInButton;
